import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID, employeeID, Dropdown } from "../../Common/UIComponents";
import { URL } from "../../Common/APIURL";
import {
  fetchSetupUtilCreateData,
  fetchSetupUtilLabList,
  fetchSetupUtilTimesheetList,
  fetchLocations,
  fetchLabsByLocation,
} from "../../API_Service/LabBooking_Slice";
import { fetchDateFormatConfig } from "../../API_Service/Settings_Slice";

// Computed once when this module first loads (i.e. once per full page
// load/refresh), not per render — so swapping a static facility image's
// file on disk under the same filename (e.g. CommonImage.png) actually
// shows up on the next page load instead of the browser silently serving
// whatever it cached for that same URL before. Re-fetches once per
// session; doesn't spam a fresh request on every re-render. Same fix as
// LabBookingLab.js's LabCard, for the same reason.
const IMG_CACHE_BUST = Date.now();

const formatDate = (dateString, format = "dd-MM-yyyy") => {
  if (!dateString) return "";

  const parts = dateString.split(" ");
  let datePart = parts[0];
  let timePart = parts.slice(1).join(" ");

  let dd, MM, yyyy, MMM;

  if (datePart.includes("-") && datePart.split("-")[0].length === 2) {
    const [d, m, y] = datePart.split("-");
    dd = d;
    MM = m;
    yyyy = y;
    const tempDate = new Date(`${y}-${m}-${d}T00:00:00`);
    MMM = tempDate.toLocaleString("en-US", { month: "short" });
  } else {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    dd = String(date.getDate()).padStart(2, "0");
    MM = String(date.getMonth() + 1).padStart(2, "0");
    MMM = date.toLocaleString("en-US", { month: "short" });
    yyyy = String(date.getFullYear());

    if (!timePart) {
      let hours = date.getHours();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutes = String(date.getMinutes()).padStart(2, '0');
      timePart = `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    }
  }

  const formattedDate = format.replace("MMM", MMM).replace("MM", MM).replace("dd", dd).replace("yyyy", yyyy);
  return timePart ? `${formattedDate} ${timePart}` : formattedDate;
};

// ─────────────────────────────────────────────
// LAB CARD — click whole card to open
// ─────────────────────────────────────────────
function LabCard({ lab, isMapped, onOpen, opening, dateFormat = "dd-MM-yyyy" }) {
  const [imgError, setImgError] = useState(false);
  const isOpening = opening === lab.facilityId;
  const isDisabled = !!opening && !isOpening;

  return (
    <button
      type="button"
      onClick={() => onOpen(lab)}
      disabled={!!opening}
      className="group relative w-full h-full text-left rounded-3xl overflow-hidden transition-all duration-300 focus:outline-none flex flex-col"
      style={{
        boxShadow: isOpening
          ? "0 0 0 3px #08757C, 0 20px 40px rgba(8,117,124,0.25)"
          : "0 4px 24px rgba(24,28,29,0.07)",
        transform: isDisabled ? "scale(0.98)" : undefined,
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {/* Card background */}
      <div className="bg-white rounded-3xl overflow-hidden border border-[#e5e9e9] group-hover:border-[#08757C]/40 transition-all duration-300 group-hover:shadow-xl flex flex-col flex-1">
        {/* Top image strip */}
        <div className="relative h-56 overflow-hidden bg-gradient-to-br from-[#08757c] to-[#004f54]">
          {lab.facilityImagePath && !imgError ? (
            <img
              src={`${URL}${lab.facilityImagePath}?v=${IMG_CACHE_BUST}`}
              alt={lab.facilityName}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="material-symbols-outlined text-white/20 text-[72px]">
                science
              </span>
            </div>
          )}
          {/* Overlay — strong toward the bottom so the facility name stays
              legible over whatever image gets uploaded (bright, busy, or
              dark backgrounds all wash out the same way underneath it). */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {isMapped && (
              <span className="text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest bg-[#08757C] text-white backdrop-blur-sm flex items-center gap-1">
                <span className="material-symbols-outlined text-[10px]">
                  star
                </span>
                My Lab
              </span>
            )}
          </div>

          {/* Opening spinner overlay */}
          {isOpening && (
            <div className="absolute inset-0 bg-[#08757C]/60 backdrop-blur-sm flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[32px] animate-spin">
                progress_activity
              </span>
            </div>
          )}

          {/* Hover arrow — top-right now that the name sits at the bottom */}
          {!isOpening && (
            <div className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0">
              <span className="material-symbols-outlined text-white text-[16px]">
                arrow_forward
              </span>
            </div>
          )}

          {/* Facility name — overlaid on the image itself so it reads
              consistently no matter what photo is behind it. */}
          <div className="absolute bottom-3 left-3 right-3">
            <h3
              className="font-extrabold text-white text-base leading-tight truncate"
              style={{
                fontFamily: "Manrope, sans-serif",
                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
              }}
            >
              {lab.facilityName}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 flex flex-col flex-1">
          {(lab.projectName || lab.createdDate) && (
            <div className="grid grid-cols-2 gap-3 mb-2">
              {lab.projectName && (
                <div className="min-w-0">
                  <p className="text-[9px] font-bold text-[#9eabac] uppercase tracking-widest">
                    Project
                  </p>
                  <p
                    className="text-[11px] text-[#3e494a] font-semibold truncate"
                    title={lab.projectName}
                  >
                    {lab.projectName}
                  </p>
                </div>
              )}
              {lab.createdDate && (
                <div className="min-w-0">
                  <p className="text-[9px] font-bold text-[#9eabac] uppercase tracking-widest">
                    Last Activity
                  </p>
                  <p
                    className="text-[11px] text-[#3e494a] font-semibold truncate"
                    title={`${lab.createdDate}${lab.time ? ` ${lab.time}` : ""}`}
                  >
                    {formatDate(lab.createdDate, dateFormat)}
                    {lab.time ? ` ${lab.time}` : ""}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Open CTA */}
          <div className="mt-auto pt-2 border-t border-[#f1f4f4] flex items-center justify-between">
            <span className="text-[11px] text-[#9eabac] font-medium">
              {isOpening ? "Opening…" : "Click to open"}
            </span>
            <div
              className="h-7 w-7 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-110"
              style={{ background: isOpening ? "#08757C40" : "#08757C" }}
            >
              <span className="material-symbols-outlined text-white text-[14px]">
                {isOpening ? "hourglass_top" : "arrow_forward"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export default function SetupUtilLocations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [mappedLabs, setMappedLabs] = useState([]);
  const [otherLabs, setOtherLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [opening, setOpening] = useState(null);
  const [search, setSearch] = useState("");
  const OTHER_LABS_PAGE_SIZE = 8;
  const [otherLabsLimit, setOtherLabsLimit] = useState(OTHER_LABS_PAGE_SIZE);

  // ── Location filter — same city dropdown + facility-by-location lookup
  // already used for the booking wizard (Lab_GetLocationList /
  // Lab_GetLabListBasedOnLocation), reused here instead of a one-off. ──
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCityID, setSelectedCityID] = useState("all");
  const [cityFacilityIds, setCityFacilityIds] = useState(null); // null = no location filter applied
  const [cityLabsLoading, setCityLabsLoading] = useState(false);

  const [userDateFormat, setUserDateFormat] = useState("dd-MM-yyyy");

  useEffect(() => {
    dispatch(fetchDateFormatConfig({ companyID }))
      .unwrap()
      .then((res) => {
        const data = res?.data ?? res;
        const config = Array.isArray(data) ? data[0] : data;
        if (config?.DateFormat) {
          setUserDateFormat(config.DateFormat);
        }
      })
      .catch(console.error);

    if (!companyID) return;
    dispatch(fetchLocations({ companyID, empID: Number(employeeID) || 0 }))
      .then((res) => {
        const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
        const rows = Array.isArray(d) ? d : [];
        setCityOptions(
          rows.map((r) => ({
            value: r.cityID,
            label: r.cityName ?? `Location ${r.cityID}`,
          })),
        );
      })
      .catch(() => { });
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCityID === "all") {
      setCityFacilityIds(null);
      return;
    }
    setCityLabsLoading(true);
    dispatch(fetchLabsByLocation({ ID: 0, locationID: selectedCityID, companyID }))
      .then((res) => {
        const d = res?.payload?.data ?? res?.payload ?? res?.data ?? res;
        const rows = Array.isArray(d) ? d : [];
        setCityFacilityIds(new Set(rows.map((l) => l.facilityId)));
      })
      .catch(() => setCityFacilityIds(new Set()))
      .finally(() => setCityLabsLoading(false));
  }, [selectedCityID, dispatch]);

  // ── Fetch ────────────────────────────────────
  const fetchList = useCallback(() => {
    setIsLoading(true);
    dispatch(fetchSetupUtilLabList({ companyID, employeeID }))
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? {};
        const mapped = data.mappedLabs ?? [];
        const mappedIds = new Set(mapped.map((l) => l.facilityId));
        setMappedLabs(mapped);
        setOtherLabs(
          (data.allLabs ?? []).filter((l) => !mappedIds.has(l.facilityId)),
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    fetchList();
  }, [location.key]); // eslint-disable-line

  // ── Open ─────────────────────────────────────
  const handleOpen = (lab) => {
    if (opening) return;
    setOpening(lab.facilityId);
    const today = new Date().toISOString().split("T")[0];

    Promise.all([
      dispatch(
        fetchSetupUtilCreateData({
          facilityID: lab.facilityId,
          companyID,
          employeeID,
        }),
      ),
      dispatch(
        fetchSetupUtilTimesheetList({
          facilityID: lab.facilityId,
          employeeID,
          companyID,
          selDate: today,
        }),
      ),
    ])
      .then(([createRes, timesheetRes]) => {
        const payload = createRes?.payload?.data ?? {};
        const data = payload.data ?? payload;
        const startTime = payload.startTime ?? null;
        const startDate = payload.startDate ?? null;
        const timesheetPayload = timesheetRes?.payload?.data ?? {};
        const timesheetData = timesheetPayload.data ?? timesheetPayload;

        navigate("/lab-management/lab-utilization", {
          state: { lab, createData: data, startTime, startDate, timesheetData },
        });
      })
      .finally(() => setOpening(null));
  };

  // ── Search + Location filter ──────────────────
  const q = search.trim().toLowerCase();
  const filter = (labs) =>
    labs
      .filter((l) => !cityFacilityIds || cityFacilityIds.has(l.facilityId))
      .filter(
        (l) =>
          !q ||
          (l.facilityName ?? "").toLowerCase().includes(q) ||
          (l.location ?? "").toLowerCase().includes(q),
      );
  const visibleMapped = filter(mappedLabs);
  const visibleOther = filter(otherLabs);
  const visibleOtherLimited = visibleOther.slice(0, otherLabsLimit);
  const hasMoreOtherLabs = visibleOther.length > otherLabsLimit;
  const total = mappedLabs.length + otherLabs.length;


  useEffect(() => {
    setOtherLabsLimit(OTHER_LABS_PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, cityFacilityIds]);

  return (
    <DashboardLayout>
      <div className="min-h-screen" style={{ background: "#f0f4f4" }}>
        <div className="px-8 pt-5 pb-4">
          <nav className="flex items-center gap-2 text-xs text-[#6e797a] mb-3">
            <span>Lab Management</span>
            <span className="material-symbols-outlined text-[14px]">
              chevron_right
            </span>
            <span className="font-semibold" style={{ color: "#08757C" }}>
              Setup Utilization
            </span>
          </nav>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1
                className="text-3xl font-black text-[#181c1d] tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Choose a Lab
              </h1>
              <p className="text-sm text-[#6e797a] mt-1">
                {total} lab{total !== 1 ? "s" : ""} available — click any card
                to open
              </p>
            </div>

            <div className="flex items-center gap-2.5">
              {/* Location filter */}
              <div className="w-44">
                <Dropdown
                  options={[
                    { value: "all", label: "All Locations" },
                    ...cityOptions,
                  ]}
                  value={selectedCityID}
                  onChange={setSelectedCityID}
                  placeholder="All Locations"
                  disabled={cityLabsLoading}
                  compact
                />
              </div>

              {/* Search */}
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9eabac] text-[17px] pointer-events-none">
                  search
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search labs…"
                  className="h-11 pl-10 pr-5 bg-white border border-[#e5e9e9] rounded-2xl text-sm text-[#181c1d] placeholder:text-[#9eabac] focus:outline-none focus:ring-2 focus:ring-[#08757C]/30 w-60 shadow-sm transition-all"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9eabac] hover:text-[#3e494a]"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      close
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 pb-8">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-[#9eabac]">
              <span className="material-symbols-outlined text-[40px] animate-spin">
                progress_activity
              </span>
              <p className="text-sm font-medium">Loading labs…</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* ── My Mapped Labs ── */}
              {visibleMapped.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "#08757C18" }}
                    >
                      <span
                        className="material-symbols-outlined text-[17px]"
                        style={{ color: "#08757C" }}
                      >
                        star
                      </span>
                    </div>
                    <div>
                      <h2 className="text-sm font-extrabold text-[#181c1d]">
                        My Mapped Labs
                      </h2>
                      <p className="text-[11px] text-[#9eabac]">
                        Labs assigned directly to you
                      </p>
                    </div>
                    <span
                      className="ml-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                      style={{ background: "#08757C18", color: "#08757C" }}
                    >
                      {visibleMapped.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {visibleMapped.map((lab) => (
                      <LabCard
                        key={lab.facilityId}
                        lab={lab}
                        isMapped
                        onOpen={handleOpen}
                        opening={opening}
                        dateFormat={userDateFormat}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Divider */}
              {visibleMapped.length > 0 && visibleOther.length > 0 && (
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-[#e5e9e9]" />
                  <span className="text-[10px] font-black text-[#9eabac] uppercase tracking-widest px-1">
                    Other Labs
                  </span>
                  <div className="flex-1 h-px bg-[#e5e9e9]" />
                </div>
              )}

              {/* ── Other Labs ── */}
              {visibleOther.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "#08757C18" }}
                    >
                      <span
                        className="material-symbols-outlined text-[17px]"
                        style={{ color: "#08757C" }}
                      >
                        lab_panel
                      </span>
                    </div>
                    <div>
                      <h2 className="text-sm font-extrabold text-[#181c1d]">
                        All Labs
                      </h2>
                      <p className="text-[11px] text-[#9eabac]">
                        All labs available in the system
                      </p>
                    </div>
                    <span
                      className="ml-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                      style={{ background: "#08757C18", color: "#08757C" }}
                    >
                      {visibleOther.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {visibleOtherLimited.map((lab) => (
                      <LabCard
                        key={lab.facilityId}
                        lab={lab}
                        isMapped={false}
                        onOpen={handleOpen}
                        opening={opening}
                        dateFormat={userDateFormat}
                      />
                    ))}
                  </div>

                  {hasMoreOtherLabs && (
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        onClick={() =>
                          setOtherLabsLimit((n) => n + OTHER_LABS_PAGE_SIZE)
                        }
                        className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-bold text-[#08757C] border border-[#08757C]/30 bg-white hover:bg-[#e4f2f2] transition-all"
                      >
                        <span className="material-symbols-outlined text-[18px]">
                          expand_more
                        </span>
                        Load More (
                        {visibleOther.length - visibleOtherLimited.length}{" "}
                        remaining)
                      </button>
                    </div>
                  )}
                </section>
              )}

              {/* No search results */}
              {visibleMapped.length === 0 && visibleOther.length === 0 && q && (
                <div className="flex flex-col items-center py-24 gap-3 text-[#9eabac]">
                  <span className="material-symbols-outlined text-[48px]">
                    search_off
                  </span>
                  <p className="text-sm font-bold text-[#3e494a]">
                    No labs match{" "}
                    <span className="text-[#08757C]">"{search}"</span>
                  </p>
                  <button
                    onClick={() => setSearch("")}
                    className="text-xs font-bold px-4 py-2 rounded-xl border border-[#e5e9e9] hover:bg-white transition-all text-[#08757C]"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {/* No labs at the selected location */}
              {visibleMapped.length === 0 &&
                visibleOther.length === 0 &&
                !q &&
                cityFacilityIds && (
                  <div className="flex flex-col items-center py-24 gap-3 text-[#9eabac]">
                    <span className="material-symbols-outlined text-[48px]">
                      location_off
                    </span>
                    <p className="text-sm font-bold text-[#3e494a]">
                      No labs found for this location
                    </p>
                    <button
                      onClick={() => setSelectedCityID("all")}
                      className="text-xs font-bold px-4 py-2 rounded-xl border border-[#e5e9e9] hover:bg-white transition-all text-[#08757C]"
                    >
                      Clear location filter
                    </button>
                  </div>
                )}

              {/* No labs at all */}
              {total === 0 && !q && (
                <div className="flex flex-col items-center py-32 gap-5">
                  <div
                    className="h-20 w-20 rounded-3xl flex items-center justify-center"
                    style={{ background: "#08757C10" }}
                  >
                    <span
                      className="material-symbols-outlined text-[40px]"
                      style={{ color: "#08757C" }}
                    >
                      lab_panel
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-extrabold text-[#181c1d]">
                      No labs available
                    </p>
                    <p className="text-sm text-[#6e797a] mt-1">
                      Contact your administrator to get labs assigned.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
